import React, { FunctionComponent, useState, useMemo, useEffect } from 'react';
import { useMasterData } from 'hooks';
import clsx from 'clsx';
import styles from './MedicationDosage.module.scss';
import SelectComponent from '../SelectComponent';
import { SelectOption } from '../SelectComponent/SelectComponent';
import EditableCounterField from '../EditableCounterField';
import { getStepForUnit, getMaxDecimalsForUnit, isValidDecimalPlaces } from './medicationDosageUnitsConfig';

interface MedicationDosageProps {
    onQuantityChange: (quantity: number, unit: number) => void;
    selectedQuantity?: number;
    selectedUnit?: number;
    className?: string;
}

const MedicationDosage: FunctionComponent<MedicationDosageProps> = props => {
    const medicationDosageUnits = useMasterData().medicationDosageUnits;
    const [quantity, setQuantity] = useState<number | undefined>(props.selectedQuantity);
    const [selectedUnit, setSelectedUnit] = useState<number | undefined>(props.selectedUnit);
    const [decimalError, setDecimalError] = useState<string | null>(null);

    const selectedUnitName = useMemo(() => {
        const unit = medicationDosageUnits.find(u => u.id === selectedUnit);
        return unit?.name ?? '';
    }, [medicationDosageUnits, selectedUnit]);

    const step = useMemo(() => getStepForUnit(selectedUnitName), [selectedUnitName]);
    const maxDecimals = useMemo(
        () => (selectedUnitName ? getMaxDecimalsForUnit(selectedUnitName) : undefined),
        [selectedUnitName]
    );

    useEffect(() => {
        if (quantity != null && selectedUnitName && maxDecimals !== undefined) {
            if (isValidDecimalPlaces(quantity, maxDecimals)) {
                setDecimalError(null);
            } else {
                setDecimalError(
                    `Maximum ${maxDecimals} decimal place${
                        maxDecimals === 1 ? '' : 's'
                    } allowed for ${selectedUnitName}.`
                );
            }
        } else {
            setDecimalError(null);
        }
    }, [quantity, selectedUnitName, maxDecimals]);

    const handleUnitChange = (option: SelectOption) => {
        const unitId = option.id as number;
        const unitName = (option.name ?? '') as string;
        setSelectedUnit(unitId);
        const newMaxDecimals = unitName ? getMaxDecimalsForUnit(unitName) : undefined;
        if (quantity != null && newMaxDecimals !== undefined) {
            if (isValidDecimalPlaces(quantity, newMaxDecimals)) {
                setDecimalError(null);
            } else {
                setDecimalError(
                    `Maximum ${newMaxDecimals} decimal place${newMaxDecimals === 1 ? '' : 's'} allowed for ${unitName}.`
                );
            }
        } else {
            setDecimalError(null);
        }
        props.onQuantityChange(quantity ?? 0, unitId);
    };

    const getUnitOptions = (): SelectOption[] => {
        return medicationDosageUnits.map(unit => ({
            id: unit.id,
            name: unit.name
        }));
    };

    const getSelectedUnitOption = (): SelectOption | undefined => {
        const unit = medicationDosageUnits.find(u => u.id === selectedUnit);
        return unit ? { id: unit.id, name: unit.name } : undefined;
    };

    const handleQuantityChange = (value: number) => {
        setQuantity(value);
        props.onQuantityChange(value, selectedUnit ?? 0);
    };

    return (
        <div className={clsx(styles.medicationDosageContainer, props.className)}>
            <label className={styles.label}>
                Dosage<span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
                <div className={styles.dosageInputContainer}>
                    <EditableCounterField
                        className={styles.dosageInput}
                        value={quantity}
                        minLimit={0}
                        step={step}
                        onChange={handleQuantityChange}
                        placeholder="Ex. 1.5"
                    />
                </div>
                <div
                    className={styles.divider}
                    aria-hidden="true"
                />
                <div className={styles.unitSelector}>
                    <SelectComponent
                        selectedOption={getSelectedUnitOption()}
                        options={getUnitOptions()}
                        onSelect={handleUnitChange}
                        placeholder="Select unit"
                        className={styles.dropdownContainer}
                        borderStyle={styles.medicationDosageBorder}
                        borderExpandedStyle={styles.medicationDosageExpanded}
                    />
                </div>
            </div>
            {decimalError && (
                <p
                    className={styles.errorMessage}
                    role="alert"
                >
                    {decimalError}
                </p>
            )}
        </div>
    );
};

export default MedicationDosage;
